/**
 * Proposal pricing math — the faithful port of the source app's calculation
 * block (line subtotals, per-line margin, the five discount-type reductions,
 * blended margin). Catalog field names are Cinch's: `price_metric` (was
 * `unit_economic_id`), `unit_cost` (was `estimated_unit_cost`).
 *
 * Pure + state-shaped: the page owns the working state (transient strings while
 * the user types) and passes it in; these helpers coerce defensively.
 */

import type { Catalog, CatalogDiscount, CatalogItem, CatalogPackage } from "./api";
import { calcGrossMargin } from "./format";

/** A line item in the builder's working state (pre-save; `_id` is client-side).
 *  Numeric fields are `number | null` to match the DS NumberInput (null = blank). */
export interface LineRow {
  _id: string;
  /** Which bundle ("package") this line belongs to; null/undefined = loose line. */
  bundle_local_id?: string | null;
  item_id?: string;
  package_id?: string;
  quantity: number | null;
  /** Show the customer "Unlimited" while `quantity` stays the internal estimate. */
  display_as_unlimited?: boolean;
  unit_price: number | null;
}

/** A bundle ("package") in the builder's working state. Mirrors the Cinch package
 *  shape so it maps 1:1 on a future push. `price` null = auto-sum member lines. */
export interface BundleRow {
  _id: string;
  name: string;
  sku: string | null;
  descr: string | null;
  customer_facing_descr: string | null;
  pricing_type: string; // "flat_fee" | "per_unit"
  price: number | null;
  price_metric: string | null;
}

/** A discount row in the builder's working state. */
export interface DiscountRow {
  _id: string;
  discount_id: string;
  target_line_local_id: string | null;
  duration_override: number | null;
  unit_overrides: Record<string, number | null>;
}

export interface CatalogIndex {
  itemById: Map<string, CatalogItem>;
  pkgById: Map<string, CatalogPackage>;
  discountById: Map<string, CatalogDiscount>;
  metricNameById: Map<string, string>;
}

export function buildIndex(catalog: Catalog): CatalogIndex {
  return {
    itemById: new Map(catalog.items.map((i) => [i.id, i])),
    pkgById: new Map(catalog.packages.map((p) => [p.id, p])),
    discountById: new Map(catalog.discounts.map((d) => [d.id, d])),
    metricNameById: new Map(catalog.price_metrics.map((m) => [m.id, m.name])),
  };
}

const num = (v: number | string | null | undefined): number => Number(v) || 0;

export const qtyOf = (li: LineRow): number => num(li.quantity);
export const priceOf = (li: LineRow): number => num(li.unit_price);
export const lineSubtotal = (li: LineRow): number => qtyOf(li) * priceOf(li);

export function isFlatFee(idx: CatalogIndex, li: LineRow): boolean {
  return !!li.package_id && idx.pkgById.get(li.package_id)?.pricing_type === "flat_fee";
}

/** The price-metric id backing a line (package's or item's). */
export function lineMetricId(idx: CatalogIndex, li: LineRow): string | null {
  if (li.package_id) return idx.pkgById.get(li.package_id)?.price_metric ?? null;
  if (li.item_id) return idx.itemById.get(li.item_id)?.price_metric ?? null;
  return null;
}

export function unitName(idx: CatalogIndex, li: LineRow): string {
  if (li.package_id) {
    const pkg = idx.pkgById.get(li.package_id);
    if (!pkg) return "—";
    if (pkg.price_metric) return idx.metricNameById.get(pkg.price_metric) ?? "—";
    return pkg.pricing_type === "flat_fee" ? "Flat Fee" : "Per Unit";
  }
  const item = li.item_id ? idx.itemById.get(li.item_id) : undefined;
  if (!item?.price_metric) return "—";
  return idx.metricNameById.get(item.price_metric) ?? "—";
}

export function productName(idx: CatalogIndex, li: LineRow): string {
  if (li.package_id) return idx.pkgById.get(li.package_id)?.name || "Package";
  if (li.item_id) return idx.itemById.get(li.item_id)?.name || "Item";
  return "Empty line";
}

/** Per-unit cost backing a line: an item's `unit_cost`, or a package's implied
 *  cost from its estimated GM (Cinch serializes it as a FRACTION 0–1, e.g. 0.42). */
export function lineUnitCost(idx: CatalogIndex, li: LineRow): number {
  if (li.package_id) {
    const pkg = idx.pkgById.get(li.package_id);
    if (!pkg) return 0;
    return pkg.price * (1 - pkg.estimated_gross_margin);
  }
  const item = li.item_id ? idx.itemById.get(li.item_id) : undefined;
  return item ? item.unit_cost : 0;
}

/** Total cost of a line = per-unit cost × quantity. */
export const lineCost = (idx: CatalogIndex, li: LineRow): number =>
  lineUnitCost(idx, li) * qtyOf(li);

/** Per-line gross margin %. Packages imply cost from their estimated GM. */
export function lineMargin(idx: CatalogIndex, li: LineRow): number {
  const price = priceOf(li);
  if (li.package_id) {
    const pkg = idx.pkgById.get(li.package_id);
    if (!pkg || price === 0) return 0;
    return calcGrossMargin(price, lineUnitCost(idx, li));
  }
  const item = li.item_id ? idx.itemById.get(li.item_id) : undefined;
  if (!item) return 0;
  return calcGrossMargin(price, item.unit_cost);
}

/** The lines that belong to a bundle, in row order. */
export const bundleLines = (bundle: BundleRow, lines: LineRow[]): LineRow[] =>
  lines.filter((l) => l.bundle_local_id === bundle._id);

/** A bundle's customer-facing monthly price: the manual override if set, else the
 *  auto-sum of its member line subtotals. */
export function bundlePrice(bundle: BundleRow, lines: LineRow[]): number {
  if (bundle.price != null) return bundle.price;
  return bundleLines(bundle, lines).reduce((s, li) => s + lineSubtotal(li), 0);
}

/** A bundle's total monthly cost — the real per-line costs of its members. */
export const bundleCost = (idx: CatalogIndex, bundle: BundleRow, lines: LineRow[]): number =>
  bundleLines(bundle, lines).reduce((s, li) => s + lineCost(idx, li), 0);

/** Does the (possibly overridden) package price still hold margin? The signal the
 *  builder needs when negotiating a rounded package price. */
export function bundleMargin(idx: CatalogIndex, bundle: BundleRow, lines: LineRow[]): number {
  const price = bundlePrice(bundle, lines);
  if (price === 0) return 0;
  return calcGrossMargin(price, bundleCost(idx, bundle, lines));
}

/** Gross monthly revenue = loose lines at their subtotal + each bundle at its
 *  (possibly overridden) price. With no bundles/overrides this equals the plain
 *  line sum, so totals are unchanged for existing proposals. A line whose bundle
 *  was removed (orphaned) falls back to loose so its revenue is never dropped. */
export function grossMrrOf(lines: LineRow[], bundles: BundleRow[] = []): number {
  const validBundleIds = new Set(bundles.map((b) => b._id));
  const looseMrr = lines.reduce(
    (s, li) =>
      li.bundle_local_id && validBundleIds.has(li.bundle_local_id) ? s : s + lineSubtotal(li),
    0,
  );
  const bundlesMrr = bundles.reduce((s, b) => s + bundlePrice(b, lines), 0);
  return looseMrr + bundlesMrr;
}

export interface Reduction {
  reduction: number;
  warning: string;
}

export interface ProposalTotals {
  grossMrr: number;
  reductions: Record<string, Reduction>;
  totalReduction: number;
  mrr: number;
  arr: number;
  blendedMargin: number;
}

export function computeTotals(
  idx: CatalogIndex,
  lines: LineRow[],
  discountRows: DiscountRow[],
  contractMonths: number,
  bundles: BundleRow[] = [],
): ProposalTotals {
  const months = contractMonths || 12;
  const grossMrr = grossMrrOf(lines, bundles);

  const reductions: Record<string, Reduction> = {};
  for (const row of discountRows) {
    const d = idx.discountById.get(row.discount_id);
    if (!d) {
      reductions[row._id] = { reduction: 0, warning: "" };
      continue;
    }
    const targetLi = row.target_line_local_id
      ? lines.find((l) => l._id === row.target_line_local_id)
      : null;
    const targetMonthly = targetLi ? lineSubtotal(targetLi) : grossMrr;
    const value = num(d.value);

    if (d.type === "percentage") {
      reductions[row._id] = { reduction: (targetMonthly * value) / 100, warning: "" };
    } else if (d.type === "fixed") {
      reductions[row._id] = { reduction: Math.min(targetMonthly, value), warning: "" };
    } else if (d.type === "tiered") {
      const qtyOk = !targetLi || qtyOf(targetLi) >= (d.min_quantity || 0);
      reductions[row._id] = {
        reduction: qtyOk ? (targetMonthly * value) / 100 : 0,
        warning: qtyOk ? "" : `Min quantity ${d.min_quantity} not met on target line.`,
      };
    } else if (d.type === "time_based") {
      const period = num(row.duration_override) || d.duration_periods || 0;
      const effective = Math.min(period, months);
      const blended = (value / 100) * (effective / months);
      reductions[row._id] = { reduction: targetMonthly * blended, warning: "" };
    } else if (d.type === "quantity_based") {
      const grants = d.unit_grants || [];
      let reduction = 0;
      const skipped: string[] = [];
      for (const g of grants) {
        const override = row.unit_overrides[g.price_metric ?? ""];
        const qty = override != null ? num(override) : num(g.quantity);
        const matchLi = targetLi
          ? lineMetricId(idx, targetLi) === g.price_metric
            ? targetLi
            : null
          : lines.find((l) => lineMetricId(idx, l) === g.price_metric);
        if (!matchLi) {
          skipped.push(idx.metricNameById.get(g.price_metric ?? "") || "unit");
          continue;
        }
        reduction += priceOf(matchLi) * qty * (g.recurrence === "one_time" ? 1 / months : 1);
      }
      reductions[row._id] = {
        reduction,
        warning: skipped.length ? `No matching line for: ${skipped.join(", ")}.` : "",
      };
    } else {
      reductions[row._id] = { reduction: 0, warning: "" };
    }
  }

  const totalReduction = Object.values(reductions).reduce((s, r) => s + (r.reduction || 0), 0);
  const mrr = Math.max(0, grossMrr - totalReduction);
  const arr = mrr * months;

  let blendedMargin = 0;
  if (mrr !== 0) {
    // Cost is the real per-line cost regardless of bundling/overrides, so a
    // package's overridden price is measured against its true underlying cost.
    const weightedCost = lines.reduce((s, li) => s + lineCost(idx, li), 0);
    blendedMargin = calcGrossMargin(mrr, weightedCost);
  }

  return { grossMrr, reductions, totalReduction, mrr, arr, blendedMargin };
}
